import { useEffect, useMemo, useRef } from "react";
import { useMutation, useQuery } from "convex/react";
import { api as convexApi } from "../../convex/_generated/api";
import type { Id } from "../../convex/_generated/dataModel";
import type { ObservedSpeaker, TranscriptEntry } from "../../shared/types.ts";
import type { RoleAuth } from "../lib/auth.ts";
import { observedSpeakersFromEntries } from "./sessionSpeakerModel.ts";

const SPEAKER_BACKFILL_BATCH_LIMIT = 500;
const SPEAKER_BACKFILL_MAX_BATCHES = 20;

interface SpeakerBackfillResult {
  processed: number;
  nextAfterTs: number | null;
  done: boolean;
}

export function useSessionSpeakers({
  auth,
  sessionId,
  entries,
  enabled,
}: {
  auth: RoleAuth;
  sessionId: string | null;
  entries: TranscriptEntry[];
  enabled: boolean;
}): ObservedSpeaker[] {
  const backfillObservedSpeakers = useMutation(convexApi.transcript.backfillObservedSpeakers);
  const observedSpeakers = useQuery(
    convexApi.transcript.getObservedSpeakers,
    enabled && sessionId ? { auth, sessionId: sessionId as Id<"sessions"> } : "skip",
  ) as ObservedSpeaker[] | undefined;
  const fallbackSpeakers = useMemo(() => observedSpeakersFromEntries(entries), [entries]);
  const speakerBackfillSessionRef = useRef<string | null>(null);

  useEffect(() => {
    if (!enabled || !sessionId || entries.length === 0) return;
    if (speakerBackfillSessionRef.current === sessionId) return;
    let cancelled = false;
    speakerBackfillSessionRef.current = sessionId;

    async function runBackfill() {
      let afterTs: number | null = null;
      for (let batch = 0; batch < SPEAKER_BACKFILL_MAX_BATCHES; batch += 1) {
        const result: SpeakerBackfillResult = await backfillObservedSpeakers({
          auth,
          sessionId: sessionId as Id<"sessions">,
          limit: SPEAKER_BACKFILL_BATCH_LIMIT,
          ...(afterTs == null ? {} : { afterTs }),
        });
        if (cancelled || result.done || result.nextAfterTs == null) return;
        afterTs = result.nextAfterTs;
      }
    }

    void runBackfill().catch(() => {
      if (!cancelled && speakerBackfillSessionRef.current === sessionId) {
        speakerBackfillSessionRef.current = null;
      }
    });
    return () => {
      cancelled = true;
    };
  }, [auth, backfillObservedSpeakers, enabled, entries.length, sessionId]);

  return observedSpeakers && observedSpeakers.length > 0 ? observedSpeakers : fallbackSpeakers;
}
